import { useAuth } from '@clerk/clerk-react';
import { useCallback } from 'react';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

export type Listing = {
  listing_id: string;
  price_per_seat_cents: number;
  quantity_available: number;
  quantity_sold: number;
  listing_note: string | null;
  game_id: string;
  seat_block_id: string;
  section: string;
  row: string;
  seats_json: string;
  section_type: string;
  seat_view_photo_url: string | null;
  opponent: string;
  game_date: string;
  kickoff_time: string | null;
  week_number: number | null;
};

export type Game = {
  game_id: string;
  season: string;
  opponent: string;
  game_date: string;
  kickoff_time: string | null;
  week_number: number | null;
  venue: string;
};

export type MapSection = {
  section: string;
  section_type: string | null;
  listing_count: number;
  min_price_cents: number;
};

/** Unauthenticated GET — used by browse pages. */
export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`);
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  return res.json() as Promise<T>;
}

/** Authenticated fetcher hook. Returns a function that adds the Clerk JWT. */
export function useApi() {
  const { getToken } = useAuth();
  return useCallback(
    async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
      const token = await getToken();
      const headers = new Headers(init.headers);
      if (token) headers.set('Authorization', `Bearer ${token}`);
      if (init.body && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
      }
      const res = await fetch(`${BASE}${path}`, { ...init, headers });
      if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
      return res.json() as Promise<T>;
    },
    [getToken]
  );
}

export function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Public media URL (seat-view photos). Safe to use directly in <img src>. */
export function seatViewUrl(seatBlockId: string): string {
  return `${BASE}/media/seat-view/${seatBlockId}`;
}

/**
 * Authenticated image fetcher. Returns an object URL for a media endpoint that
 * requires the Clerk JWT (e.g. admin verification photos), since an <img> tag
 * can't send an Authorization header. Caller should revoke the URL when done.
 */
export function useAuthedImage() {
  const { getToken } = useAuth();
  return useCallback(
    async (path: string): Promise<string> => {
      const token = await getToken();
      const res = await fetch(`${BASE}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return URL.createObjectURL(await res.blob());
    },
    [getToken]
  );
}
